import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { MiDetailFields, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiArrowULeftTop } from "@mdi/js";
import { Typography, useTheme } from "@mui/material";

const undoCompletionSchema = z.object({
  reason: z.string().min(1, "Reason is required."),
});

type UndoCompletionFormValues = z.infer<typeof undoCompletionSchema>;

interface UndoCompletionModalProps {
  open: boolean;
  onClose: () => void;
  onUndo: (data: UndoCompletionFormValues) => Promise<void>;
  isLoading?: boolean;
}

export const UndoCompletionModal = ({
  open,
  onClose,
  onUndo,
  isLoading = false,
}: UndoCompletionModalProps) => {
  const theme = useTheme();

  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm<UndoCompletionFormValues>({
    mode: "onChange",
    defaultValues: {
      reason: "",
    },
    resolver: zodResolver(undoCompletionSchema),
  });

  const undoCompletionFields = useMemo(
    () => [
      {
        label: "Reason",
        name: "reason",
        required: true,
        multiline: true,
        rows: 2,
        formControl: control,
      },
    ],
    [control]
  );

  const handleFormSubmit = async (data: UndoCompletionFormValues) => {
    await onUndo(data);
    handleClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <MiModal
      open={open}
      closeModal={handleClose}
      width={480}
      titleIcon={mdiArrowULeftTop}
      leftIconColor={theme.palette.primary.main}
      title="Undo Completion"
      loading={isLoading}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: handleClose,
        },
        {
          name: "Undo Completion",
          variant: BUTTON_VARIANTS.CONTAINED,
          disabled: !isValid || !isDirty || isLoading,
          onClick: handleSubmit(handleFormSubmit),
        },
      ]}
    >
      <Typography
        variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
        color="grey"
        mb={2}
        px={2}
        fontSize={theme.fontSize.medium}
      >
        Undoing the completion will clear the completion date and move the order
        back into assigned tab.
      </Typography>
      <MiDetailFields detailFields={undoCompletionFields} />
    </MiModal>
  );
};
